import type {Container} from "konva/lib/Container.js";
import React from "react";
import {Line, Rect, Text} from "react-konva";
import {type Annotation, isSquareAnnotation} from "../ImageAnnotation.js";
import {shapeService} from "../shape.service.js";

type Props = {
  width: number;
  height: number;
  values: Annotation[];
  editingIndex?: number;
  disabled: boolean;
  stageRef: React.RefObject<Container | null>;
  onChange: (annotations: Annotation[]) => void;
  onSelect: (index: number) => void;
};
export function Shapes({
  width,
  height,
  values,
  editingIndex,
  disabled,
  stageRef,
  onChange,
  onSelect,
}: Props) {
  return (
    <>
      {values.map((annotation, index) => {
        const {x, y} = shapeService.getTextLocation(width, height, annotation);
        return (
          <React.Fragment key={index}>
            {isSquareAnnotation(annotation) && (
              <Rect
                id={`annotation-${index}`}
                x={annotation.x}
                y={annotation.y}
                width={annotation.width}
                height={annotation.height}
                stroke={annotation.color}
                strokeWidth={2}
                draggable={!disabled}
                onClick={() => {
                  handleSelect(index);
                }}
                onTap={() => {
                  handleSelect(index);
                }}
                onDragMove={(e) => handleDragMove(index, e)}
                onTransformEnd={handleTransformEnd}
              />
            )}
            {!isSquareAnnotation(annotation) && (
              <Line
                x={annotation.x}
                y={annotation.y}
                points={annotation.points}
                stroke={annotation.color}
                strokeWidth={4}
                tension={0.5}
                hitStrokeWidth={24} // Add this line - creates a wider hit area
                lineCap="round"
                lineJoin="round"
                draggable={!disabled}
                onClick={() => {
                  handleSelect(index);
                }}
                onTap={() => {
                  handleSelect(index);
                }}
                onDragMove={(e) => handleDragMove(index, e)}
              />
            )}
            {editingIndex !== index && (
              <Text
                x={x}
                y={y}
                text={annotation.label}
                fontSize={15}
                fill={annotation.color}
                onClick={() => handleSelect(index)}
                onTap={() => handleSelect(index)}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );

  function handleSelect(index: number) {
    if (disabled) {
      return;
    }
    onSelect(index);
  }

  function handleTransformEnd() {
    if (disabled) {
      return;
    }
    if (editingIndex === undefined) {
      return;
    }
    const node = stageRef.current?.findOne(`#annotation-${editingIndex}`);
    if (!node) {
      return;
    }

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    const updatedAnnotations = values.map((ann, i) => {
      if (i === editingIndex) {
        if ("x" in ann) {
          return {
            ...ann,
            x: node.x(),
            y: node.y(),
            width: node.width() * scaleX,
            height: node.height() * scaleY,
          };
        }
      }
      return ann;
    });

    onChange(updatedAnnotations);
  }

  function handleDragMove(index: number, e: any) {
    if (disabled) {
      return;
    }

    const updatedAnnotations = values.map((ann, i) => {
      if (i === index) {
        return {
          ...ann,
          x: e.target.x(),
          y: e.target.y(),
        };
      }
      return ann;
    });
    onChange(updatedAnnotations);
  }
}
